import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Input } from '../../components/UI';
import ThemeToggle from '../../components/UI/ThemeToggle';
import toast from 'react-hot-toast';
import { authAPI } from '../../services/api';
import {
  HiOutlineMail,
  HiOutlineLockClosed,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineShieldCheck,
  HiOutlineKey,
  HiOutlineArrowLeft,
} from 'react-icons/hi';
import logo from '../../assets/logo.png';

// Forgot-password flow steps
const FP_STEP = {
  LOGIN: 'login',
  EMAIL: 'email',   // enter email → OTP sent to Gmail
  OTP: 'otp',       // enter the 6-digit OTP from the email
  RESET: 'reset',   // set the new password
};

const Login = () => {
  const [mode, setMode] = useState(FP_STEP.LOGIN);

  // Login form state
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, loginWithOrganization } = useAuth();
  const navigate = useNavigate();

  // Account Switcher: when the same email/password matches more than one
  // organization, this holds the list to pick from instead of a normal login.
  const [orgPickerOptions, setOrgPickerOptions] = useState(null);
  const [orgPickerLoading, setOrgPickerLoading] = useState(false);

  // Forgot-password flow state
  const [fpEmail, setFpEmail] = useState('');
  const [fpOtp, setFpOtp] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpConfirmPassword, setFpConfirmPassword] = useState('');
  const [fpShowPassword, setFpShowPassword] = useState(false);
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState('');
  const [fpResetToken, setFpResetToken] = useState('');
  const [fpVerified, setFpVerified] = useState(false); // OTP verified → token held
  const [resendIn, setResendIn] = useState(0); // resend countdown (seconds)

  // Countdown timer for the Resend OTP button
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [resendIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!identifier.trim()) {
      setError('Please enter your email or phone number');
      return;
    }

    // Validate identifier - must be valid email OR valid phone
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[\d\s\-+]+$/;
    const trimmedIdentifier = identifier.trim();

    if (!emailRegex.test(trimmedIdentifier) && !phoneRegex.test(trimmedIdentifier)) {
      setError('Please enter a valid email address or phone number');
      return;
    }

    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(identifier.trim(), password);

      if (result?.requiresOrgSelection) {
        setOrgPickerOptions(result.organizations);
      } else if (result && result.success) {
        toast.success(
          result.role === 'telecaller'
            ? 'Welcome! Redirecting...'
            : 'Welcome Admin! Redirecting...'
        );
        navigate('/app/', { replace: true });
      } else {
        // Display error from result using both state and toast
        const errorMessage = result?.error || 'Login failed. Please try again.';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Login handleSubmit error:', err);
      const errorMsg = 'An unexpected error occurred. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectOrganization = async (organizationId) => {
    setOrgPickerLoading(true);
    setError('');
    try {
      const result = await loginWithOrganization(identifier.trim(), password, organizationId);
      if (result && result.success) {
        toast.success('Welcome! Redirecting...');
        navigate('/app/', { replace: true });
      } else {
        const errorMessage = result?.error || 'Login failed. Please try again.';
        setError(errorMessage);
        toast.error(errorMessage);
        setOrgPickerOptions(null);
      }
    } finally {
      setOrgPickerLoading(false);
    }
  };

  const handleIdentifierChange = (e) => {
    const value = e.target.value;
    // Allow both email and phone characters
    setIdentifier(value);
    setError('');
  };

  // ══════════════════════════════════════════════════════════════════════
  // FORGOT PASSWORD — Gmail OTP verification flow
  // Step 1 (email): send OTP → Step 2 (otp): verify OTP → Step 3 (reset):
  // set new password → back to login with the new password
  // ══════════════════════════════════════════════════════════════════════

  const openForgotPassword = () => {
    setMode(FP_STEP.EMAIL);
    setFpEmail(identifier.includes('@') ? identifier.trim() : '');
    setFpOtp('');
    setFpNewPassword('');
    setFpConfirmPassword('');
    setFpResetToken('');
    setFpVerified(false);
    setFpError('');
  };

  const backToLogin = () => {
    setMode(FP_STEP.LOGIN);
    setFpError('');
  };

  // Step 1 — send the OTP to the account's email
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setFpError('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(fpEmail.trim())) {
      setFpError('Please enter a valid email address');
      return;
    }

    setFpLoading(true);
    try {
      const response = await authAPI.forgotPassword(fpEmail.trim());
      // Server always returns success (prevents email enumeration) unless
      // the account is deactivated / email sending failed
      if (response.data?.success) {
        toast.success('OTP sent to your email — check your Gmail inbox');
        setMode(FP_STEP.OTP);
        setResendIn(60);
      } else {
        const msg = response.data?.message || 'Failed to send OTP. Please try again.';
        setFpError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to send OTP. Please try again.';
      setFpError(msg);
      toast.error(msg);
    } finally {
      setFpLoading(false);
    }
  };

  // Step 2 — verify the OTP → server returns a short-lived reset token
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setFpError('');

    if (!fpOtp.trim() || fpOtp.trim().length < 4) {
      setFpError('Please enter the OTP from your email');
      return;
    }

    setFpLoading(true);
    try {
      const response = await authAPI.verifyOtp(fpEmail.trim(), fpOtp.trim());
      if (response.data?.success && response.data?.data?.resetToken) {
        setFpResetToken(response.data.data.resetToken);
        setFpVerified(true);
        toast.success('OTP verified — set your new password');
        setMode(FP_STEP.RESET);
      } else {
        const msg = response.data?.message || 'Invalid OTP. Please try again.';
        setFpError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Invalid OTP. Please try again.';
      setFpError(msg);
      toast.error(msg);
    } finally {
      setFpLoading(false);
    }
  };

  // Step 2b — resend OTP (server rate-limits to 3 per 10 minutes)
  const handleResendOtp = async () => {
    if (resendIn > 0) return;
    setFpError('');
    setFpLoading(true);
    try {
      const response = await authAPI.resendOtp(fpEmail.trim());
      if (response.data?.success) {
        toast.success('New OTP sent to your email');
        setResendIn(60);
      } else {
        const msg = response.data?.message || 'Failed to resend OTP.';
        setFpError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to resend OTP.';
      setFpError(msg);
      toast.error(msg);
    } finally {
      setFpLoading(false);
    }
  };

  // Step 3 — set the new password, then login with it
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setFpError('');

    if (fpNewPassword.length < 6) {
      setFpError('Password must be at least 6 characters long');
      return;
    }
    if (fpNewPassword !== fpConfirmPassword) {
      setFpError('Passwords do not match');
      return;
    }

    setFpLoading(true);
    try {
      const response = await authAPI.resetPassword(
        fpResetToken,
        fpNewPassword,
        fpConfirmPassword,
      );
      if (response.data?.success) {
        toast.success('Password reset successfully! Login with your new password.');
        setPassword(fpNewPassword);
        setIdentifier(fpEmail.trim());
        setMode(FP_STEP.LOGIN);
        setFpError('');
      } else {
        const msg = response.data?.message || 'Failed to reset password.';
        setFpError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to reset password.';
      setFpError(msg);
      toast.error(msg);
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-600 via-indigo-700 to-primary-800 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-48 sm:w-72 md:w-96 h-48 sm:h-72 md:h-96 bg-white/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-64 sm:w-80 md:w-[500px] h-64 sm:h-80 md:h-[500px] bg-indigo-400/20 rounded-full blur-3xl transform translate-x-1/3 translate-y-1/3" />
        <div className="absolute top-1/2 left-1/2 w-40 sm:w-56 md:w-64 h-40 sm:h-56 md:h-64 bg-primary-300/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Content */}
      <div className="relative flex-1 flex flex-col justify-center px-4 py-8 sm:py-12">
        {/* Dark / light mode toggle — moon = dark, sun = light */}
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="mx-auto w-full max-w-sm sm:max-w-md">
          {/* Logo */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="brand-logo-box inline-flex items-center justify-center lg:w-[300px] lg:h-[80px] w-60 rounded-2xl bg-white backdrop-blur-sm mb-4 shadow-2xl ring-1 ring-white/20 overflow-hidden">
              <img src={logo} alt="TeleValley" className="w-full h-full object-contain p-1.5 sm:p-2" />
            </div>
          </div>

          {/* Login Card */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-5 sm:p-8 ring-1 ring-white/20">

            {/* ════════════ MODE: LOGIN ════════════ */}
            {mode === FP_STEP.LOGIN && orgPickerOptions && (
              <>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2">
                  Choose an Organization
                </h2>
                <p className="text-center text-gray-500 text-sm mb-6 sm:mb-8">
                  This email is used in more than one organization — pick which one to sign into.
                </p>
                {error && (
                  <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-300">
                    <p className="text-sm text-red-700 text-center font-medium">{error}</p>
                  </div>
                )}
                <div className="space-y-2">
                  {orgPickerOptions.map((org) => (
                    <button
                      key={org.organizationId}
                      type="button"
                      disabled={orgPickerLoading}
                      onClick={() => handleSelectOrganization(org.organizationId)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-gray-200 hover:border-primary-400 hover:bg-primary-50/30 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {org.logo ? (
                        <img src={org.logo} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 font-bold flex items-center justify-center flex-shrink-0">
                          {org.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      <span className="font-semibold text-gray-900">{org.name}</span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => { setOrgPickerOptions(null); setError(''); }}
                  className="mt-4 w-full text-center text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  Back to login
                </button>
              </>
            )}

            {mode === FP_STEP.LOGIN && !orgPickerOptions && (
              <>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2">
                  Welcome Back
                </h2>
                <p className="text-center text-gray-500 text-sm mb-6 sm:mb-8">
                  Sign in to access your dashboard
                </p>

                {/* Error Message */}
                {error && (
                  <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-300">
                    <p className="text-sm text-red-700 text-center font-medium">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                  <Input
                    label="Email or Phone Number"
                    type="text"
                    value={identifier}
                    onChange={handleIdentifierChange}
                    placeholder="Enter your email or phone"
                    icon={<HiOutlineMail className="w-5 h-5" />}
                    disabled={isLoading}
                  />

                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                    placeholder="Enter your password"
                    icon={<HiOutlineLockClosed className="w-5 h-5" />}
                    disabled={isLoading}
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-400 hover:text-gray-600 p-1 focus:outline-none"
                      >
                        {showPassword ? (
                          <HiOutlineEyeOff className="w-5 h-5" />
                        ) : (
                          <HiOutlineEye className="w-5 h-5" />
                        )}
                      </button>
                    }
                  />

                  {/* Forgot Password link */}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={openForgotPassword}
                      className="text-sm font-semibold text-primary-600 hover:text-primary-700 focus:outline-none"
                    >
                      Forgot Password?
                    </button>
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-600">Remember me</span>
                    </label>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    loading={isLoading}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Signing in...' : 'Sign in'}
                  </Button>
                </form>

                {/* Register Link */}
                <div className="mt-5 text-center">
                  <p className="text-sm text-gray-600">
                    Don't have an account?{' '}
                    <Link to="/register" className="font-semibold text-primary-600 hover:text-primary-700">
                      Create one
                    </Link>
                  </p>
                </div>
              </>
            )}

            {/* ════════════ MODE: FORGOT — STEP 1 (EMAIL) ════════════ */}
            {mode === FP_STEP.EMAIL && (
              <>
                <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-50 mx-auto mb-4">
                  <HiOutlineKey className="w-7 h-7 text-primary-600" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2">
                  Forgot Password?
                </h2>
                <p className="text-center text-gray-500 text-sm mb-6">
                  Enter your account email — we'll send a verification code (OTP) to your Gmail
                </p>

                {fpError && (
                  <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-300">
                    <p className="text-sm text-red-700 text-center font-medium">{fpError}</p>
                  </div>
                )}

                <form onSubmit={handleSendOtp} className="space-y-4 sm:space-y-5">
                  <Input
                    label="Account Email"
                    type="email"
                    value={fpEmail}
                    onChange={(e) => {
                      setFpEmail(e.target.value);
                      setFpError('');
                    }}
                    placeholder="Enter your registered email"
                    icon={<HiOutlineMail className="w-5 h-5" />}
                    disabled={fpLoading}
                    autoFocus
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    loading={fpLoading}
                    disabled={fpLoading}
                  >
                    {fpLoading ? 'Sending OTP...' : 'Send OTP'}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={backToLogin}
                    icon={<HiOutlineArrowLeft className="w-4 h-4" />}
                  >
                    Back to Login
                  </Button>
                </form>
              </>
            )}

            {/* ════════════ MODE: FORGOT — STEP 2 (OTP) ════════════ */}
            {mode === FP_STEP.OTP && (
              <>
                <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-50 mx-auto mb-4">
                  <HiOutlineShieldCheck className="w-7 h-7 text-primary-600" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2">
                  Verify OTP
                </h2>
                <p className="text-center text-gray-500 text-sm mb-6">
                  Enter the 6-digit code sent to{' '}
                  <span className="font-semibold text-gray-700">{fpEmail}</span>
                </p>

                {fpError && (
                  <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-300">
                    <p className="text-sm text-red-700 text-center font-medium">{fpError}</p>
                  </div>
                )}

                <form onSubmit={handleVerifyOtp} className="space-y-4 sm:space-y-5">
                  <Input
                    label="Verification Code (OTP)"
                    type="text"
                    inputMode="numeric"
                    value={fpOtp}
                    onChange={(e) => {
                      // Digits only, max 6
                      setFpOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setFpError('');
                    }}
                    placeholder="Enter 6-digit OTP"
                    icon={<HiOutlineShieldCheck className="w-5 h-5" />}
                    disabled={fpLoading}
                    autoFocus
                    className="text-center"
                    style={{ letterSpacing: '0.4em', fontWeight: 700 }}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    loading={fpLoading}
                    disabled={fpLoading}
                  >
                    {fpLoading ? 'Verifying...' : 'Verify OTP'}
                  </Button>

                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={backToLogin}
                      className="flex items-center gap-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      <HiOutlineArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resendIn > 0 || fpLoading}
                      className="font-semibold text-primary-600 hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none"
                    >
                      {resendIn > 0 ? `Resend OTP in ${resendIn}s` : 'Resend OTP'}
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* ════════════ MODE: FORGOT — STEP 3 (NEW PASSWORD) ════════════ */}
            {mode === FP_STEP.RESET && (
              <>
                <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-green-50 mx-auto mb-4">
                  <HiOutlineLockClosed className="w-7 h-7 text-green-600" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2">
                  Set New Password
                </h2>
                <p className="text-center text-gray-500 text-sm mb-6">
                  OTP verified. Choose a new password for{' '}
                  <span className="font-semibold text-gray-700">{fpEmail}</span>
                </p>

                {fpError && (
                  <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-300">
                    <p className="text-sm text-red-700 text-center font-medium">{fpError}</p>
                  </div>
                )}

                <form onSubmit={handleResetPassword} className="space-y-4 sm:space-y-5">
                  <Input
                    label="New Password"
                    type={fpShowPassword ? 'text' : 'password'}
                    value={fpNewPassword}
                    onChange={(e) => {
                      setFpNewPassword(e.target.value);
                      setFpError('');
                    }}
                    placeholder="At least 6 characters"
                    icon={<HiOutlineLockClosed className="w-5 h-5" />}
                    disabled={fpLoading}
                    autoFocus
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setFpShowPassword(!fpShowPassword)}
                        className="text-gray-400 hover:text-gray-600 p-1 focus:outline-none"
                      >
                        {fpShowPassword ? (
                          <HiOutlineEyeOff className="w-5 h-5" />
                        ) : (
                          <HiOutlineEye className="w-5 h-5" />
                        )}
                      </button>
                    }
                  />

                  <Input
                    label="Confirm New Password"
                    type={fpShowPassword ? 'text' : 'password'}
                    value={fpConfirmPassword}
                    onChange={(e) => {
                      setFpConfirmPassword(e.target.value);
                      setFpError('');
                    }}
                    placeholder="Re-enter the new password"
                    icon={<HiOutlineLockClosed className="w-5 h-5" />}
                    disabled={fpLoading}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    loading={fpLoading}
                    disabled={fpLoading}
                  >
                    {fpLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={backToLogin}
                    icon={<HiOutlineArrowLeft className="w-4 h-4" />}
                  >
                    Back to Login
                  </Button>
                </form>
              </>
            )}
          </div>

          {/* Bottom Info */}
          <div className="mt-6 sm:mt-8 text-center">
            <p className="text-xs text-white/50">
              © {new Date().getFullYear()} TeleValley. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;